import { useTheme, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { headerFormats, headerModules } from "./enums/TextEditorHeader";
import logger from "../../utils/logger";

const MAX_LENGTH = 5000;
const StyledReactQuill = styled(ReactQuill)(({ theme }) => {
  const bgColor = theme.palette?.background?.paper || "#ffffff";
  const textColor = theme.palette?.text?.primary || "#000000";
  const dividerColor = theme.palette?.divider || "#e0e0e0";
  const disabledColor = theme.palette?.text?.disabled || "#bdbdbd";
  const borderRadius = theme.shape?.borderRadius || 4;

  return {
    width: "100%",
    maxWidth: "100%",

    "& .ql-toolbar": {
      width: "100%",
      boxSizing: "border-box",
      backgroundColor: bgColor,
      borderColor: dividerColor,
      borderTopLeftRadius: borderRadius,
      borderTopRightRadius: borderRadius,
    },

    "& .ql-container": {
      overflowY: "auto",
      width: "100%",
      boxSizing: "border-box",
      backgroundColor: bgColor,
      borderColor: dividerColor,
      borderBottomLeftRadius: borderRadius,
      borderBottomRightRadius: borderRadius,

      "& .ql-editor": {
        minHeight: "300px",
        width: "100%",
        boxSizing: "border-box",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        color: textColor,
        fontFamily: theme.typography?.fontFamily || "inherit",

        "&.ql-blank::before": {
          color: disabledColor,
          fontStyle: "normal",
        },
      },
    },
  };
});

export default function TextEditorInput({
  value,
  height = "450px",
  disable,
  onChange,
  testId,
  ...field
}) {
  const reactQuillRef = useRef(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const [charCount, setCharCount] = useState(0);

  // Verify theme context is available
  useEffect(() => {
    if (reactQuillRef.current) {
      const editor = reactQuillRef.current.getEditor();
      const text = editor.getText();
      setCharCount(text.trim().length);
    }
  }, [value]);

  const handleChange = (content, delta, source, editor) => {
    const text = editor.getText();
    const length = text.trim().length;

    if (length <= MAX_LENGTH) {
      setCharCount(length);
      onChange?.(DOMPurify.sanitize(content));
    } else {
      const quill = reactQuillRef.current?.getEditor();
      if (quill) {
        const trimmed = text.slice(0, MAX_LENGTH);
        quill.setText(trimmed);
        setCharCount(MAX_LENGTH);
      }
    }
  };

  const handleKeyPress = (e) => {
    const editor = reactQuillRef.current?.getEditor();
    if (editor) {
      const text = editor.getText();
      if (
        text.length >= MAX_LENGTH &&
        e.key !== "Backspace" &&
        e.key !== "Delete"
      ) {
        e.preventDefault();
      }
    }
  };

  return (
    <Box width="100%" display="flex" flexDirection="column" data-testid={testId}>
      <StyledReactQuill
        theme="snow"
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        value={DOMPurify.sanitize(value ?? "")}
        ref={reactQuillRef}
        modules={headerModules}
        formats={headerFormats}
        label={t("WriteText")}
        readOnly={disable}
        style={{
          height: height,
          minHeight: height,
          width: "100%",
        }}
        {...field}
      />

      <Box mt={6}>
        <Typography
          variant="caption"
          display="block"
          textAlign="right"
          color={charCount >= MAX_LENGTH ? "error" : "text.secondary"}
        >
          {charCount} / {MAX_LENGTH}
        </Typography>
      </Box>
    </Box>
  );
}
